import React from "react";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import FormControl from "@mui/joy/FormControl";
import FormHelperText from "@mui/joy/FormHelperText";
import FormLabel from "@mui/joy/FormLabel";
import Grid from "@mui/joy/Grid";
import Input from "@mui/joy/Input";
import Option from "@mui/joy/Option";
import Select from "@mui/joy/Select";
import Sheet from "@mui/joy/Sheet";
import Stack from "@mui/joy/Stack";
import Switch from "@mui/joy/Switch";
import Typography from "@mui/joy/Typography";
import { NotificationService } from "../../../common/notification";
import {
    AccountSelection,
    AdminExternalProviderConnection,
    AdminExternalProviderInfo,
} from "../../../models/externalMarkets";
import { parseOptionalNumber } from "../components/shared";
import { extProviderConnectionApi } from "../externalMarketsApi";

type NumberKey = {
    [K in keyof AdminExternalProviderConnection]-?: AdminExternalProviderConnection[K] extends number ? K : never;
}[keyof AdminExternalProviderConnection];

type NumberField = { key: NumberKey; label: string; hint: string; min: number; max?: number };

/** Основное: то, что меняют чаще всего */
const MAIN_FIELDS: NumberField[] = [
    { key: "rateLimitPerMinute", label: "Запросов в минуту на аккаунт", hint: "Лимит поставщика на один ключ API", min: 1 },
    { key: "maxConcurrentRequests", label: "Запросов одновременно на аккаунт", hint: "Сколько запросов держать в полёте", min: 1 },
    { key: "timeoutSeconds", label: "Таймаут запроса, с", hint: "От 1 до 300", min: 1, max: 300 },
];

/** Повторы, прокси, остывание аккаунтов и живой фид */
const ADVANCED_FIELDS: NumberField[] = [
    { key: "retryCount", label: "Повторов при 5xx и сетевых ошибках", hint: "Только для запросов, которые безопасно повторять", min: 0 },
    { key: "retryBaseDelayMilliseconds", label: "Пауза между повторами, мс", hint: "Растёт с номером попытки", min: 0 },
    { key: "maxAccountSwitches", label: "Смен аккаунта на запрос", hint: "Для чтения без привязки к аккаунту: после лимита или отказа ключа", min: 0 },
    { key: "maxInFlightRetries", label: "Повторов «запрос уже в полёте»", hint: "429 без Retry-After", min: 0 },
    { key: "inFlightRetryDelayMilliseconds", label: "Пауза перед таким повтором, мс", hint: "Растёт с номером попытки", min: 0 },
    { key: "maxRetryAfterWaitSeconds", label: "Макс. ожидание Retry-After, с", hint: "Дольше запрос аккаунта не ждёт и возвращает 429", min: 0 },
    { key: "maxProxyRotations", label: "Смен прокси на запрос", hint: "Если прокси не отвечает", min: 0 },
    { key: "proxyRevalidateSeconds", label: "Перечитывать прокси, с", hint: "Как часто сверять хост арендованного прокси с БД", min: 1 },
    { key: "accountsReloadSeconds", label: "Перечитывать аккаунты, с", hint: "Чтобы видеть изменения из других процессов", min: 1 },
    { key: "circuitBreakerFailureThreshold", label: "Ошибок подряд до остывания", hint: "После них аккаунт отдыхает", min: 1 },
    { key: "circuitBreakerBaseCooldownSeconds", label: "Первое остывание, с", hint: "Дальше удваивается", min: 1 },
    { key: "circuitBreakerMaxCooldownSeconds", label: "Макс. остывание, с", hint: "Не меньше первого", min: 1 },
    { key: "liveFeedReconnectBaseDelayMilliseconds", label: "Фид: первая пауза переподключения, мс", hint: "Дальше удваивается", min: 1 },
    { key: "liveFeedReconnectMaxDelaySeconds", label: "Фид: макс. пауза переподключения, с", hint: "Предел паузы", min: 1 },
    { key: "liveFeedIdleTimeoutSeconds", label: "Фид: тишина до переподключения, с", hint: "Столько секунд без сообщений — соединение переоткрывается", min: 1 },
];

const ALL_FIELDS = [...MAIN_FIELDS, ...ADVANCED_FIELDS];

type Props = {
    provider: AdminExternalProviderInfo;
    onChanged: () => void;
};

function isHttpUrl(value: string): boolean {
    try {
        const url = new URL(value.trim());
        return url.protocol === "http:" || url.protocol === "https:";
    } catch {
        return false;
    }
}

function isWsUrl(value: string): boolean {
    try {
        const url = new URL(value.trim());
        return url.protocol === "ws:" || url.protocol === "wss:";
    } catch {
        return false;
    }
}

export function ConnectionSettings(props: Props) {
    const { provider, onChanged } = props;
    const [values, setValues] = React.useState<Record<NumberKey, string>>({} as Record<NumberKey, string>);
    const [baseUrl, setBaseUrl] = React.useState("");
    const [liveFeedUrl, setLiveFeedUrl] = React.useState("");
    const [liveFeedEnabled, setLiveFeedEnabled] = React.useState(true);
    const [selection, setSelection] = React.useState<AccountSelection>("RoundRobin");
    const [advanced, setAdvanced] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [serverError, setServerError] = React.useState<string>();

    React.useEffect(() => {
        const c = provider.connection;
        if (!c) {
            return;
        }
        const next = {} as Record<NumberKey, string>;
        ALL_FIELDS.forEach((f) => (next[f.key] = String(c[f.key] ?? "")));
        setValues(next);
        setBaseUrl(c.baseUrl ?? "");
        setLiveFeedUrl(c.liveFeedUrl ?? "");
        setLiveFeedEnabled(c.liveFeedEnabled);
        setSelection(c.readAccountSelection === "LeastRecentlyUsed" ? "LeastRecentlyUsed" : "RoundRobin");
        setServerError(undefined);
    }, [provider]);

    const invalidFields = ALL_FIELDS.filter((f) => {
        const n = parseOptionalNumber(values[f.key] ?? "");
        return n === undefined || !Number.isInteger(n) || n < f.min || (f.max !== undefined && n > f.max);
    }).map((f) => f.key);
    const baseUrlInvalid = !isHttpUrl(baseUrl);
    const liveFeedUrlInvalid = liveFeedUrl.trim() !== "" && !isWsUrl(liveFeedUrl);
    const hasErrors = invalidFields.length > 0 || baseUrlInvalid || liveFeedUrlInvalid;
    const advancedHasErrors = ADVANCED_FIELDS.some((f) => invalidFields.includes(f.key)) || liveFeedUrlInvalid;

    const renderField = (f: NumberField) => (
        <Grid key={f.key} xs={12} sm={6}>
            <FormControl size="sm" error={invalidFields.includes(f.key)}>
                <FormLabel>{f.label}</FormLabel>
                <Input
                    value={values[f.key] ?? ""}
                    onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                />
                <FormHelperText>{f.hint}</FormHelperText>
            </FormControl>
        </Grid>
    );

    const save = async () => {
        const request = {
            baseUrl: baseUrl.trim(),
            liveFeedUrl: liveFeedUrl.trim() || undefined,
            liveFeedEnabled,
            readAccountSelection: selection,
        } as AdminExternalProviderConnection;
        ALL_FIELDS.forEach((f) => (request[f.key] = parseOptionalNumber(values[f.key]) ?? 0));

        setSaving(true);
        setServerError(undefined);
        try {
            const res = await extProviderConnectionApi(provider.key, request);
            if (res.result) {
                NotificationService.alertSuccess("Подключение к поставщику сохранено, службы подхватят его без перезапуска");
                onChanged();
            } else {
                setServerError(res.error ?? "Не удалось сохранить подключение");
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Sheet variant="outlined" sx={{ p: 2, borderRadius: "sm", maxWidth: 720 }}>
            <Stack spacing={2}>
                <Box>
                    <Typography level="title-md">Подключение к API</Typography>
                    <Typography level="body-xs">
                        Раньше задавалось в appsettings служб. Изменения применяются в API и фоновой службе без перезапуска.
                    </Typography>
                </Box>
                <FormControl size="sm" error={baseUrlInvalid}>
                    <FormLabel>Адрес API</FormLabel>
                    <Input value={baseUrl} placeholder="https://api.rustskins.com" onChange={(e) => setBaseUrl(e.target.value)} />
                    <FormHelperText>
                        {baseUrlInvalid ? "Нужен абсолютный http(s)-адрес" : "Без него поставщик не синхронизируется и ключи не проверяются"}
                    </FormHelperText>
                </FormControl>
                <Grid container spacing={1.5}>
                    {MAIN_FIELDS.map(renderField)}
                    <Grid xs={12} sm={6}>
                        <FormControl size="sm">
                            <FormLabel>Выбор аккаунта для чтения</FormLabel>
                            <Select value={selection} onChange={(_, v) => v && setSelection(v)}>
                                <Option value="RoundRobin">По кругу</Option>
                                <Option value="LeastRecentlyUsed">Дольше всех не использовавшийся</Option>
                            </Select>
                            <FormHelperText>Для покупок аккаунт всегда выбирается по балансу</FormHelperText>
                        </FormControl>
                    </Grid>
                </Grid>
                <Box>
                    <Button
                        size="sm"
                        variant="plain"
                        color={advancedHasErrors ? "danger" : "neutral"}
                        onClick={() => setAdvanced(!advanced)}
                    >
                        {advanced ? "Скрыть дополнительные" : "Дополнительные: повторы, прокси, остывание, живой фид"}
                    </Button>
                </Box>
                {advanced && (
                    <Stack spacing={1.5}>
                        <FormControl orientation="horizontal" sx={{ gap: 2, alignItems: "center" }}>
                            <Box>
                                <FormLabel>Живой фид</FormLabel>
                                <FormHelperText>Держать соединение с фидом поставщика и ресинкать витрину по событиям</FormHelperText>
                            </Box>
                            <Switch checked={liveFeedEnabled} onChange={(e) => setLiveFeedEnabled(e.target.checked)} />
                        </FormControl>
                        <FormControl size="sm" error={liveFeedUrlInvalid}>
                            <FormLabel>Адрес фида</FormLabel>
                            <Input value={liveFeedUrl} placeholder="wss://..." onChange={(e) => setLiveFeedUrl(e.target.value)} />
                            <FormHelperText>
                                {liveFeedUrlInvalid ? "Нужен абсолютный ws(s)-адрес" : "Пусто — строится из адреса API"}
                            </FormHelperText>
                        </FormControl>
                        <Grid container spacing={1.5}>
                            {ADVANCED_FIELDS.map(renderField)}
                        </Grid>
                    </Stack>
                )}
                {serverError && (
                    <Typography level="body-sm" color="danger">
                        {serverError}
                    </Typography>
                )}
                <Box>
                    <Button loading={saving} disabled={hasErrors} onClick={save}>
                        Сохранить подключение
                    </Button>
                </Box>
            </Stack>
        </Sheet>
    );
}
